import React, { useState, useEffect } from 'react';
import { Workflow, VibeProcess, Task, TaskStatus, EmployeeRecord, CustomProperty, CustomPropertyValue } from 'winslow-core';
import { Button } from '../../../ds2/components';
import WorkflowDiagram from '../vibe-coder/WorkflowDiagram';
import { apiGetVibeProcess, apiGetWorkflow } from '../../../services/WorkflowService';
import { apiGetTasksForWorkflow } from '../../../services/TaskService';
import { Eye, Code, X, Info } from 'lucide-react';
import './AppWorkflowViewer.css';
import { erGetCPValue } from '../app-employee-records/employeeRecordUtils';
import { apiGetCustomPropertiesForWorkspace, apiGetEmployeeRecordsForWorkspace } from '../../../services/EmployeeRecordService';
import { useSelector } from 'react-redux';
import { selectUser } from '../../../redux/features/appData';
import { MyTooltip } from '../../common';
import { AppCustomProperty } from '../app-customize/AppCustomProperty';

type AppWorkflowViewerProps = {
  workflowId: string;
  onClose: () => void;
};

export const AppWorkflowViewer = ({ workflowId, onClose }: AppWorkflowViewerProps) => {

  const storeUser = useSelector(selectUser);
  const [activeTab, setActiveTab] = useState<'overview' | 'workflow'>('overview');
  const [vibeProcess, setVibeProcess] = useState<VibeProcess | null>(null);
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [showWorkflowDiagram, setShowWorkflowDiagram] = useState(false);
  const [showWorkflowSource, setShowWorkflowSource] = useState(false);

  const [employeeRecords, setEmployeeRecords] = useState<EmployeeRecord[]>([]);
  const [customProperties, setCustomProperties] = useState<CustomProperty[]>([]);
  const [propertyValues, setPropertyValues] = useState<Map<string, any>>(new Map());

  useEffect(() => {
    if (employeeRecords.length === 0) {
    (async () => {
        const ers = await apiGetEmployeeRecordsForWorkspace(storeUser?.currentWorkspaceId || "");
        setEmployeeRecords(ers || []);

        await loadWorkflowData();
      })();
    }
  }, [workflowId]);

  const loadWorkflowData = async () => {
    try {
      const [workflowData, tasksData] = await Promise.all([
        apiGetWorkflow(workflowId),
        apiGetTasksForWorkflow(workflowId)
      ]);


      if (!tasksData) {
        setTasks([]);
        return;
      }

      setWorkflow(workflowData);
      setTasks(tasksData);

      const vibeProcess = await apiGetVibeProcess(workflowData?.vibeProcessId || "");
      setVibeProcess(vibeProcess);

      const workspaceCPs = await apiGetCustomPropertiesForWorkspace(storeUser?.currentWorkspaceId || "");

      // console.log("[AppWorkspaceCPs] workspaceCPs: ", workspaceCPs);
      const cpsLabels = JSON.parse(vibeProcess?.customProperties || "[]").map((cp: any) => cp.label);

      // console.log("[AppWorkspaceCPs] cpsLabels: ", cpsLabels);
      const cps = workspaceCPs?.filter((cp: CustomProperty) => cpsLabels.includes(cp.label));
      // console.log("[AppWorkspaceCPs] final cps: ", cps);
      setCustomProperties(cps || []);

      const pvsFinal = new Map<string, any>();

      const pvs = JSON.parse(workflowData?.customProperties || "[]") as CustomPropertyValue[];

      console.log("[AppWorkflowViewer] pvs: ", pvs);
      pvs.forEach((pv: any) => {
        pvsFinal.set(pv.label, pv.data);
      });

      setPropertyValues(pvsFinal);
    } catch (error) {
      console.error('Error loading workflow data:', error);
    } finally {
    }
  };

  const getTaskStatusText = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.ACTIVE:
        return 'Active';
      case TaskStatus.COMPLETE:
        return 'Complete';
      case TaskStatus.REJECTED:
        return 'Rejected';
      case TaskStatus.WAITING:
        return 'Waiting';
      default:
        return 'Unknown';
    }
  };

  const getTaskStatusColor = (status: TaskStatus) => {
    switch (status) {
      case TaskStatus.ACTIVE:
        return '#f59e0b';
      case TaskStatus.COMPLETE:
        return '#10b981';
      case TaskStatus.REJECTED:
        return '#ef4444';
      case TaskStatus.WAITING:
        return '#6b7280';
      default:
        return '#6b7280';
    }
  };

  function getEmployeeName(employeeRecordId: string): React.ReactNode {
    const employeeRecord = employeeRecords.find(er => er.id === employeeRecordId);

    if (!employeeRecord) {
      return "Unknown Creator";
    }

    return `${erGetCPValue(employeeRecord, "system-firstName")} ${erGetCPValue(employeeRecord, "system-lastName")}`;
  }

  // if (loading) {
  //   return (
  //     <div className="workflow-viewer-modal">
  //       <div className="workflow-viewer-content">
  //         <div className="workflow-viewer-loading">
  //           <p>Loading workflow...</p>
  //         </div>
  //       </div>
  //     </div>
  //   );
  // }

  // console.log("[AppWorkflowViewer] workflowId: ", workflowId);
  // console.log("[AppWorkflowViewer] workflow: ", workflow);
  // console.log("[AppWorkflowViewer] tasks: ", tasks);
  // console.log("[AppWorkflowViewer] employeeRecords: ", employeeRecords);

  console.log("[AppWorkflowViewer] customProperties: ", customProperties);
  console.log("[AppWorkflowViewer] propertyValues: ", propertyValues);


  if (!workflow) {
    return (
      <div className="workflow-viewer-modal">
        <div className="workflow-viewer-content">
          <div className="workflow-viewer-error">
            <p>Failed to load workflow</p>
            <Button label="Close" onClick={onClose} />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="workflow-viewer-modal">
      <div className="workflow-viewer-content">
        {/* Header */}
        <div className="workflow-viewer-header">
          <h2>{workflow.vibeProcess?.name || 'N/A'}</h2>
          <Button 
            label="Close" 
            leftIcon={<X size={16} />}
            onClick={onClose} 
          />
        </div>

        {/* Tabs */}
        <div className="workflow-viewer-tabs">
          <button
            className={`workflow-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`workflow-tab ${activeTab === 'workflow' ? 'active' : ''}`}
            onClick={() => setActiveTab('workflow')}
          >
            Workflow
          </button>
        </div>

        {/* Tab Content */}
        <div className="workflow-viewer-tab-content">
          {activeTab === 'overview' && (
            <div className="workflow-overview">
   
              <div className="workflow-custom-properties">
                {customProperties.map((customProperty: CustomProperty, index: number) => (
                  <div
                    key={customProperty.label}
                    className="app-vibe-process-editor-property"
                  >
                    <div className="workflow-overview-custom-property">
                      <div className="app-vibe-process-editor-property-label">
                        {customProperty.display}
                      </div>
                      {customProperty.description && (
                        <MyTooltip title={customProperty.description}>
                          <Info size={16} className="app-vibe-process-editor-info-icon" />
                        </MyTooltip>
                      )}
                    
                      {!customProperty.required && (
                        <div className="app-vibe-process-editor-property-optional">
                          OPTIONAL
                        </div>
                      )}
                    
                      <div className="app-vibe-process-editor-property-middle">
                        <AppCustomProperty
                          customProperty={customProperty}
                          data={propertyValues.get(customProperty.label) || undefined}
                          validate={false}
                          disabled={true}
                          onChange={() => {}}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'workflow' && (
            <div className="workflow-details">
              

              {/* <div className="workflow-steps">
                <h3>Workflow Steps</h3>
                <div className="workflow-steps-content">
                  <pre>{workflow.steps}</pre>
                </div>
              </div> */}

              <div className="workflow-tasks">
                {/* <h3>Tasks ({tasks.length})</h3> */}
                <div className="workflow-tasks-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Task Name</th>
                        <th>Owner</th>
                        <th>Status</th>
                        <th>Type</th>
                        <th>Created</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.length === 0 ? (
                        <tr>
                          <td colSpan={5} className="no-tasks">
                            No tasks found for this workflow
                          </td>
                        </tr>
                      ) : (
                        tasks.map((task) => (
                          <tr key={task.id}>
                            <td>{task.name}</td>
                            <td>{getEmployeeName(task.ownerEmployeeRecordId)}</td>
                            <td>
                              <span 
                                className="task-status-badge"
                                style={{ color: getTaskStatusColor(task.status) }}
                              >
                                {getTaskStatusText(task.status)}
                              </span>
                            </td>
                            <td>{task.isApproval ? 'Approval' : 'Task'}</td>
                            <td>{new Date(task.createdAt).toLocaleDateString()}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              <div className="workflow-actions-container">
                {/* <div className="workflow-actions"> */}
                  <Button
                    label="View Flow"
                    variant="secondary"
                    leftIcon={<Eye size={16} />}
                    onClick={() => setShowWorkflowDiagram(true)}
                  />
                  <Button
                    label="Source"
                    variant="secondary"
                    leftIcon={<Code size={16} />}
                    onClick={() => setShowWorkflowSource(true)}
                  />
                {/* </div> */}
              </div>
            </div>
          )}
        </div>

        {/* Modals */}
        {showWorkflowDiagram && (
          <div className="workflow-diagram-modal">
            <div className="workflow-diagram-content">
              <div className="workflow-diagram-header">
                <h3>Workflow Flow Chart</h3>
                <Button 
                  label="Close" 
                  leftIcon={<X size={16} />}
                  onClick={() => setShowWorkflowDiagram(false)} 
                />
              </div>
              <div className="workflow-diagram-body">
                {workflow.vibeProcess?.workflowDiagram ? (
                  <WorkflowDiagram workflowData={workflow.vibeProcess.workflowDiagram} />
                ) : (
                  <p>No workflow diagram available</p>
                )}
              </div>
            </div>
          </div>
        )}

        {showWorkflowSource && (
          <div className="workflow-source-modal">
            <div className="workflow-source-content">
              <div className="workflow-source-header">
                <h3>Workflow Diagram Source</h3>
                <Button 
                  label="Close" 
                  leftIcon={<X size={16} />}
                  onClick={() => setShowWorkflowSource(false)} 
                />
              </div>
              <div className="workflow-source-body">
                <pre>{JSON.stringify(JSON.parse(workflow.vibeProcess?.workflowDiagram || '{}'), null, 2)}</pre>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
